import { useEffect } from "react";
import { SITE_NAME, SITE_OG_IMAGE, SITE_URL } from "../seo/config";

/**
 * Gestión del <head> por página.
 *
 * Antes esto usaba react-helmet-async, pero en esta combinación de versiones
 * no llegaba a escribir nada en el DOM: ni las meta por ruta ni los bloques
 * JSON-LD (cero elementos con `data-rh` en la página renderizada). El
 * resultado era que las ocho rutas compartían el title, la descripción y el
 * canonical de index.html, y que el sitio no publicaba ningún dato
 * estructurado.
 *
 * La sustitución escribe sobre el <head> directamente. Es menos elegante que
 * un componente declarativo, pero es determinista y no depende de que una
 * librería siga comportándose igual entre versiones.
 *
 * Criterio: las etiquetas que ya existen en index.html se ACTUALIZAN en vez de
 * duplicarse, para que el HTML servido siga teniendo un valor por defecto
 * correcto antes de que arranque el JS.
 */

const MANAGED = "data-omega-seo";

/** Crea o actualiza una etiqueta única identificada por selector. */
const upsert = (selector, create, apply) => {
  let node = document.head.querySelector(selector);
  if (!node) {
    node = create();
    node.setAttribute(MANAGED, "");
    document.head.appendChild(node);
  }
  apply(node);
};

const setMetaByName = (name, content) => {
  if (!content) return;
  upsert(
    `meta[name="${name}"]`,
    () => {
      const el = document.createElement("meta");
      el.setAttribute("name", name);
      return el;
    },
    (el) => el.setAttribute("content", content)
  );
};

const setMetaByProperty = (property, content) => {
  if (!content) return;
  upsert(
    `meta[property="${property}"]`,
    () => {
      const el = document.createElement("meta");
      el.setAttribute("property", property);
      return el;
    },
    (el) => el.setAttribute("content", content)
  );
};

const setLink = (rel, href, hreflang) => {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  upsert(
    selector,
    () => {
      const el = document.createElement("link");
      el.setAttribute("rel", rel);
      if (hreflang) el.setAttribute("hreflang", hreflang);
      return el;
    },
    (el) => el.setAttribute("href", href)
  );
};

const Seo = ({ title, description, keywords, path = "/", jsonLd = [], noindex = false }) => {
  const canonical = `${SITE_URL}${path === "/" ? "/" : path}`;
  const graphs = JSON.stringify(jsonLd);

  useEffect(() => {
    document.title = title;
    document.documentElement.lang = "es-CL";

    setMetaByName("description", description);
    setMetaByName("keywords", keywords);
    setMetaByName(
      "robots",
      noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    );
    setMetaByName("author", SITE_NAME);
    setMetaByName("geo.region", "CL-RM");
    setMetaByName("geo.placename", "Santiago, Chile");

    setLink("canonical", canonical);
    setLink("alternate", canonical, "es-CL");
    setLink("alternate", canonical, "x-default");

    setMetaByProperty("og:type", "website");
    setMetaByProperty("og:url", canonical);
    setMetaByProperty("og:title", title);
    setMetaByProperty("og:description", description);
    setMetaByProperty("og:locale", "es_CL");
    setMetaByProperty("og:site_name", SITE_NAME);
    setMetaByProperty("og:image", SITE_OG_IMAGE);
    setMetaByProperty("og:image:alt", title);

    setMetaByName("twitter:card", "summary_large_image");
    setMetaByName("twitter:title", title);
    setMetaByName("twitter:description", description);
    setMetaByName("twitter:image", SITE_OG_IMAGE);

    // Los bloques JSON-LD de la ruta anterior se retiran completos: mezclar el
    // breadcrumb de una página con el de otra confunde a los rastreadores.
    document
      .head.querySelectorAll(`script[${MANAGED}]`)
      .forEach((node) => node.remove());

    JSON.parse(graphs).forEach((data) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute(MANAGED, "");
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    });
  }, [title, description, keywords, canonical, graphs, noindex]);

  return null;
};

export default Seo;
